import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@/shared/ui/badge';
import type { ListColumn } from '@/shared/app-list-layout/types';
import type { DataTableView } from '@/shared/app-list-layout';
import { formatDate } from '@/shared/format';
import { agreementsPlatformApi } from '@/modules/agreements/services/platform-api';
import {
  AgreementsList,
  executionStatusLabel,
  executionStatusTone,
  requiredActionLabel,
} from '@/modules/agreements/shared';
import type { AgreementExecutionResponse } from '@/api/schemas/agreements.types';
import type { AgreementExecutionStatus } from '@/api/schemas/agreements.types';

/**
 * The platform's execution ledger.
 *
 * PLATFORM authority: `platform.agreement.execution.read`. This is a read of what subjects
 * have done — it is not a way to act on their behalf.
 *
 * The page renders through the shared `AppListLayout` with the same surface the tenant
 * directory carries: saved-view tabs with counts, a search box, column-bound filters,
 * numeric pagination and the table/cards toggle. The read is a bounded whole-collection
 * read — the newest `EXECUTION_PAGE_SIZE` executions — so the page answers the engine's
 * query locally, and `onFetch` stays the single data path.
 */

/** The ledger read is bounded, so the page reads one page of executions. */
const EXECUTION_PAGE_SIZE = 50;

/** The statuses a ledger row can carry. */
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
];

/**
 * The fields a search term is matched against — what identifies an execution to a human.
 *
 * The status is matched by its WORD, not by its wire value: a user types "declined",
 * never "declined" as a code.
 */
function searchFields(row: AgreementExecutionResponse): ReadonlyArray<string> {
  return [
    row.executionRef,
    row.subjectKind,
    requiredActionLabel(row.requiredAction),
    executionStatusLabel(row.status),
  ];
}

/** Saved views rendered as tabs above the ledger. */
function buildExecutionViews(counts: Partial<Record<string, number>>): DataTableView[] {
  return [
    { id: 'all', label: 'All executions', count: counts.all },
    { id: 'pending', label: 'Pending', filters: { status: 'pending' }, count: counts.pending },
    {
      id: 'completed',
      label: 'Completed',
      filters: { status: 'completed' },
      count: counts.completed,
    },
    { id: 'declined', label: 'Declined', filters: { status: 'declined' }, count: counts.declined },
    { id: 'expired', label: 'Expired', filters: { status: 'expired' }, count: counts.expired },
  ];
}

/** The page's columns. Declarative descriptors — the engine renders every cell. */
function executionColumns(
  onOpen: (executionId: string) => void,
): ReadonlyArray<ListColumn<AgreementExecutionResponse>> {
  return [
    {
      id: 'reference',
      header: 'Reference',
      // The reference is what identifies an execution — it is the heading a card leads
      // with, not a labelled field buried under one.
      primary: true,
      sortable: true,
      cell: (row) => (
        <button
          type="button"
          className="text-left font-mono text-xs text-accent hover:underline"
          onClick={() => {
            onOpen(row.executionId);
          }}
        >
          {row.executionRef}
        </button>
      ),
    },
    {
      id: 'subject',
      header: 'Subject',
      filter: {
        type: 'select',
        options: [
          { value: 'tenant', label: 'Tenant' },
          { value: 'customer', label: 'Customer' },
        ],
        placeholder: 'Any',
      },
      cell: (row) => <span className="capitalize">{row.subjectKind}</span>,
    },
    {
      id: 'action',
      header: 'Action',
      sortable: true,
      cell: (row) => requiredActionLabel(row.requiredAction),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      filter: { type: 'select', options: STATUS_OPTIONS, placeholder: 'Any' },
      cell: (row) => (
        <Badge tone={executionStatusTone(row.status)}>{executionStatusLabel(row.status)}</Badge>
      ),
    },
    {
      id: 'completed',
      header: 'Completed',
      sortable: true,
      cell: (row) => (
        <span className="text-content-muted">{row.completedAt ? formatDate(row.completedAt) : '—'}</span>
      ),
    },
  ];
}

export function ExecutionsListPage(): ReactNode {
  const navigate = useNavigate();
  const [status, setStatus] = useState<AgreementExecutionStatus | undefined>(undefined);

  const read = useCallback(
    () =>
      agreementsPlatformApi
        .listExecutions({ limit: EXECUTION_PAGE_SIZE, status })
        .then(({ executions }) => executions),
    [status],
  );

  const onOpen = useCallback(
    (executionId: string): void => {
      void navigate({
        to: '/platform/agreements/executions/$executionId',
        params: { executionId },
      });
    },
    [navigate],
  );
  const columns = useMemo(() => executionColumns(onOpen), [onOpen]);

  // The ledger read is narrowed by the server, so the status filter is pushed into
  // the request rather than applied to the page already on screen. The engine's
  // filter values are strings; the API's status is a closed union, so the value is
  // only accepted when it names one of the statuses the ledger can carry.
  const handleStatusChange = useCallback((next: string | undefined): void => {
    setStatus(STATUS_OPTIONS.some((option) => option.value === next) ? (next as AgreementExecutionStatus) : undefined);
  }, []);

  return (
    <AgreementsList<AgreementExecutionResponse>
      title="Agreement executions"
      description="What subjects have done with the agreements they were asked to act on."
      read={read}
      searchFields={searchFields}
      countKeyOf={(row) => row.status}
      buildViews={buildExecutionViews}
      onStatusChange={handleStatusChange}
      columns={columns}
      getRowId={(row) => row.executionId}
      // Five columns run off a phone screen, so the page asks for the responsive
      // presentation: cards below `sm`, the table above.
      responsive
      emptyTitle="No executions"
      emptyDescription="No agreement executions match this filter."
      noMatchDescription="No execution matches this tab, search or filter."
    />
  );
}
